import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    HiOutlinePlus, HiOutlineSearch, HiX, HiOutlineEmojiSad, HiOutlineReceiptTax, HiOutlinePaperClip, HiOutlineDotsVertical,
    HiOutlineDuplicate, HiOutlineTrash, HiOutlineUpload, HiOutlineDocumentText, HiOutlineExternalLink, HiOutlineCash,
    HiOutlineShoppingBag, HiTrendingUp, HiTrendingDown, HiOutlineInformationCircle, HiOutlineLockClosed
} from 'react-icons/hi';
import AuthenticationService from '../service/AuthenticationService';
import ExpenseService from '../service/ExpenseService';
import ShopService from '../service/ShopService';
import SidePanel from '../components/SidePanel';
import Modal from '../components/Modal';
import Toast from '../components/Toast';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import { formatRupee } from '../components/Price';
import {
    EXPENSE_CATEGORIES, EXPENSE_GROUPS, EXPENSE_UNITS, PAID_BY, PERIODS, categoryOf, unitLabel, paidByLabel,
    periodRange, periodKeyOf, todayIso, nextMonthIso, formatDay, formatMonth
} from '../util/expenseTypes';
import '../css/admin.css'
import '../css/expenses.css'

const PAGE_SIZE = 50
const MAX_RECEIPT = 5 * 1024 * 1024
const RECEIPT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
const ISO = /^\d{4}-\d{2}-\d{2}$/

const EMPTY_FORM = { amount: '', spentOn: '', category: '', unit: '', title: '', vendor: '', billNo: '', paidBy: '', notes: '' }

const serverMessage = (error, fallback) => (error && error.response && error.response.data && error.response.data.msg) || fallback

const signed = (value) => {
    const n = Number(value) || 0
    return (n < 0 ? '−' : '+') + formatRupee(Math.abs(n))
}

// ---------- summary (admins) ----------

const Tile = ({ icon, label, value, note, tone, loading }) => (
    <div className={'mv-exp-tile ' + tone}>
        <span className='mv-exp-tile-icon' aria-hidden='true'>{icon}</span>
        <span className='mv-exp-tile-label'>{label}</span>
        {loading ? <Skeleton width='70%' height={34} /> : <span className='mv-exp-tile-value'>{value}</span>}
        <span className='mv-exp-tile-note'>{loading ? ' ' : note}</span>
    </div>
)

const Summary = ({ summary, loading, category, unit, onCategory, onUnit }) => {
    const s = summary || {}
    const net = Number(s.net) || 0
    const sales = Number(s.sales) || 0
    const total = Number(s.total) || 0
    const byCategory = s.byCategory || []
    const months = s.months || []
    const maxCategory = Math.max(1, ...byCategory.map(c => Number(c.amount) || 0))
    const maxMonth = Math.max(1, ...months.flatMap(m => [Number(m.sales) || 0, Number(m.expenses) || 0]))
    const margin = sales > 0 ? Math.round((net / sales) * 100) : null

    return (
        <section className='mv-exp-summary' aria-label='Summary for the period'>
            <div className='mv-exp-tiles'>
                <Tile icon={<HiOutlineCash />} label='Spent' tone='is-spent' loading={loading}
                    value={formatRupee(total) || '₹0'} note={`${byCategory.length} ${byCategory.length === 1 ? 'category' : 'categories'}`} />
                <Tile icon={<HiOutlineShoppingBag />} label='Sales' tone='is-sales' loading={loading}
                    value={formatRupee(sales) || '₹0'} note='Paid orders, not cancelled or returned' />
                <Tile icon={net >= 0 ? <HiTrendingUp /> : <HiTrendingDown />} label={net >= 0 ? 'Net · profit' : 'Net · loss'}
                    tone={net >= 0 ? 'is-profit' : 'is-loss'} loading={loading} value={signed(net)}
                    note={margin == null ? 'No sales in this period' : `${margin < 0 ? '−' : ''}${Math.abs(margin)}% of sales`} />
            </div>

            <div className='mv-exp-insights'>
                <div className='mv-exp-card'>
                    <div className='mv-exp-card-head'>
                        <h2>Where the money went</h2>
                        {category && <button type='button' className='mv-link-btn' onClick={() => onCategory('')}>Show all</button>}
                    </div>
                    {loading ? <>
                        <div className='mv-exp-stack mv-exp-stack-bars'>{[0, 1, 2, 3].map(i => <Skeleton key={i} height={47} radius='8px' />)}</div>
                        <div className='mv-exp-units'><Skeleton width='70%' height={40} radius='999px' /></div>
                    </> :
                        byCategory.length === 0 ? <p className='mv-exp-muted'>Nothing spent in this period yet.</p> :
                            <ul className='mv-exp-bars'>
                                {byCategory.map(c => {
                                    const cat = categoryOf(c.key)
                                    const amount = Number(c.amount) || 0
                                    const share = total > 0 ? Math.round((amount / total) * 100) : 0
                                    return (
                                        <li key={c.key}>
                                            <button type='button' className={'mv-exp-bar' + (category === c.key ? ' active' : '')}
                                                aria-pressed={category === c.key} title={`${cat.label}: ${formatRupee(amount)} (${share}%)`}
                                                onClick={() => onCategory(category === c.key ? '' : c.key)}>
                                                <span className='mv-exp-bar-label'><span aria-hidden='true'>{cat.icon}</span>{cat.label}</span>
                                                <span className='mv-exp-bar-value'>{formatRupee(amount)} <small>{share}%</small></span>
                                                <span className='mv-exp-bar-track'>
                                                    <span className='mv-exp-bar-fill' style={{ width: `${Math.max(2, (amount / maxCategory) * 100)}%` }} />
                                                </span>
                                            </button>
                                        </li>
                                    )
                                })}
                            </ul>}
                    {!loading && (s.byUnit || []).length > 0 &&
                        <div className='mv-exp-units' role='group' aria-label='By unit'>
                            {s.byUnit.map(u =>
                                <button type='button' key={u.key} className={'mv-exp-unit' + (unit === u.key ? ' active' : '')}
                                    aria-pressed={unit === u.key} onClick={() => onUnit(unit === u.key ? '' : u.key)}>
                                    <span>{unitLabel(u.key)}</span><strong>{formatRupee(u.amount)}</strong>
                                </button>)}
                        </div>}
                </div>

                <div className='mv-exp-card'>
                    <div className='mv-exp-card-head'>
                        <h2>Last 6 months</h2>
                        <span className='mv-exp-legend' aria-hidden='true'>
                            <span><i className='is-sales' />Sales</span>
                            <span><i className='is-spent' />Spent</span>
                        </span>
                    </div>
                    {loading ? <div className='mv-exp-stack mv-exp-stack-months'>{[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} height={45} radius='8px' />)}</div> :
                        <ul className='mv-exp-months'>
                            {months.map(m => {
                                const sold = Number(m.sales) || 0
                                const spent = Number(m.expenses) || 0
                                const n = Number(m.net) || 0
                                return (
                                    <li key={m.month} className='mv-exp-month'
                                        title={`${formatMonth(m.month, true)}: sales ${formatRupee(sold)}, spent ${formatRupee(spent)}, net ${signed(n)}`}>
                                        <span className='mv-exp-month-name'>{formatMonth(m.month)}</span>
                                        <span className='mv-exp-month-bars'>
                                            <span className='mv-exp-month-row'>
                                                <span className='mv-exp-month-track'><span className='mv-exp-month-fill is-sales' style={{ width: `${(sold / maxMonth) * 100}%` }} /></span>
                                                <span className='mv-exp-month-num'><span className='mv-visually-hidden'>Sales </span>{formatRupee(sold)}</span>
                                            </span>
                                            <span className='mv-exp-month-row'>
                                                <span className='mv-exp-month-track'><span className='mv-exp-month-fill is-spent' style={{ width: `${(spent / maxMonth) * 100}%` }} /></span>
                                                <span className='mv-exp-month-num'><span className='mv-visually-hidden'>Spent </span>{formatRupee(spent)}</span>
                                            </span>
                                        </span>
                                        <span className={'mv-exp-net ' + (n >= 0 ? 'is-profit' : 'is-loss')}>
                                            <span className='mv-visually-hidden'>{n >= 0 ? 'Profit ' : 'Loss '}</span>{signed(n)}
                                        </span>
                                    </li>
                                )
                            })}
                        </ul>}
                </div>
            </div>
            <p className='mv-exp-footnote'>
                <HiOutlineInformationCircle aria-hidden='true' /> Net = sales − expenses paid. Stock you buy counts when it is paid for.
            </p>
        </section>
    )
}

// ---------- list ----------

const ExpenseRow = ({ expense, isAdmin, menuOpen, onOpen, onMenu, onDuplicate, onDelete, flash }) => {
    const cat = categoryOf(expense.category)
    const meta = [expense.vendor, unitLabel(expense.unit), paidByLabel(expense.paidBy)].filter(Boolean).join(' · ')
    return (
        <li className={'mv-exp-row' + (flash ? ' is-flash' : '')}>
            <button type='button' className='mv-exp-row-main' onClick={() => onOpen(expense)}>
                <span className='mv-exp-row-icon' aria-hidden='true' title={cat.label}>{cat.icon}</span>
                <span className='mv-exp-row-text'>
                    <span className='mv-exp-row-title'>
                        {expense.title}
                        {expense.hasReceipt && <HiOutlinePaperClip className='mv-exp-clip' aria-label='Has a receipt' />}
                    </span>
                    <span className='mv-exp-row-meta'>
                        <span className='mv-exp-row-cat'>{cat.label}</span>{meta && ' · ' + meta}
                    </span>
                    {isAdmin && expense.createdByName && <span className='mv-exp-row-by'>Added by {expense.createdByName}</span>}
                </span>
                <span className='mv-exp-row-amount'>{formatRupee(expense.amount)}</span>
            </button>
            <div className='mv-exp-menu-wrap'>
                <button type='button' className='mv-icon-btn mv-exp-menu-btn' aria-label={`More for ${expense.title}`}
                    aria-haspopup='menu' aria-expanded={menuOpen} onClick={(e) => { e.stopPropagation(); onMenu(menuOpen ? null : expense.id) }}>
                    <HiOutlineDotsVertical />
                </button>
                {menuOpen &&
                    <div className='mv-exp-menu' role='menu' onClick={(e) => e.stopPropagation()}>
                        <button type='button' role='menuitem' onClick={() => onDuplicate(expense)}>
                            <HiOutlineDuplicate aria-hidden='true' /> Duplicate
                        </button>
                        <button type='button' role='menuitem' className='is-danger' onClick={() => onDelete(expense)}>
                            <HiOutlineTrash aria-hidden='true' /> Delete
                        </button>
                    </div>}
            </div>
        </li>
    )
}

const ListSkeleton = () => (
    <div className='mv-exp-list' aria-hidden='true'>
        {[0, 1].map(g =>
            <section key={g} className='mv-exp-day'>
                <Skeleton width={140} height={16} style={{ margin: '4px 0 10px' }} />
                <div className='mv-exp-day-card'>
                    {[0, 1, 2].map(i =>
                        <div key={i} className='mv-exp-row-skel'>
                            <Skeleton width={40} height={40} radius='10px' />
                            <span className='mv-exp-row-skel-text'><Skeleton width='60%' /><Skeleton width='40%' height={12} /></span>
                            <Skeleton width={64} height={18} />
                        </div>)}
                </div>
            </section>)}
    </div>
)

// ---------- page ----------

const Expenses = () => {
    const history = useHistory()
    const location = useLocation()
    const isAdmin = AuthenticationService.getRole() === 'ADMIN'

    const params = new URLSearchParams(location.search)
    const fallback = periodRange('month')
    const from = ISO.test(params.get('from') || '') ? params.get('from') : fallback.from
    const to = ISO.test(params.get('to') || '') ? params.get('to') : fallback.to
    const period = periodKeyOf(from, to)

    const [customOpen, setCustomOpen] = useState(period === 'custom')
    const [search, setSearch] = useState('')
    const [q, setQ] = useState('')
    const [category, setCategory] = useState('')
    const [unit, setUnit] = useState('')

    const [list, setList] = useState(null)       // { items, page, totalPages, totalCount, sum }
    const [listError, setListError] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [summary, setSummary] = useState(null)
    const [summaryLoading, setSummaryLoading] = useState(isAdmin)
    const listReq = useRef(0)
    const summaryReq = useRef(0)

    const [menuId, setMenuId] = useState(null)
    const [editor, setEditor] = useState(null)  // { mode: 'new' | 'edit' | 'duplicate', expense }
    const [form, setForm] = useState(EMPTY_FORM)
    const [formError, setFormError] = useState('')
    const [saving, setSaving] = useState(false)
    const [receipt, setReceipt] = useState(null)  // { file, url, name, type, existing, loading }
    const [progress, setProgress] = useState(null)
    const [confirmDelete, setConfirmDelete] = useState(null)
    const [deleting, setDeleting] = useState(false)
    const [toast, setToast] = useState('')
    const [flashId, setFlashId] = useState(null)
    const [brands, setBrands] = useState([])
    const amountRef = useRef(null)
    const fileRef = useRef(null)

    // ---- data ----

    useEffect(() => {
        const timer = setTimeout(() => setQ(search.trim()), 300)
        return () => clearTimeout(timer)
    }, [search])

    const loadList = useCallback((page = 0) => {
        const id = ++listReq.current
        if (page > 0) setLoadingMore(true)
        setListError(false)
        return ExpenseService.list({ from, to, category, unit, q }, page, PAGE_SIZE)
            .then(res => {
                if (id !== listReq.current) return
                const data = (res && res.data && res.data.data) || {}
                const items = data.items || []
                setList(prev => ({
                    ...data,
                    items: page > 0 && prev ? [...prev.items, ...items.filter(x => !prev.items.some(p => p.id === x.id))] : items
                }))
            })
            .catch(() => { if (id === listReq.current) setListError(true) })
            .finally(() => { if (id === listReq.current) setLoadingMore(false) })
    }, [from, to, category, unit, q])

    const loadSummary = useCallback(() => {
        if (!isAdmin) return
        const id = ++summaryReq.current
        setSummaryLoading(true)
        ExpenseService.summary(from, to)
            .then(res => { if (id === summaryReq.current) setSummary((res && res.data && res.data.data) || null) })
            .catch(() => { if (id === summaryReq.current) setSummary(null) })
            .finally(() => { if (id === summaryReq.current) setSummaryLoading(false) })
    }, [from, to, isAdmin])

    useEffect(() => { loadList(0) }, [loadList])
    useEffect(() => { loadSummary() }, [loadSummary])

    useEffect(() => {
        ShopService.getBrands()
            .then(res => setBrands(Array.isArray(res.data.data) ? res.data.data : []))
            .catch(() => setBrands([]))
    }, [])

    // close the row menu on any outside click or Escape
    useEffect(() => {
        if (menuId == null) return undefined
        const close = () => setMenuId(null)
        const onKey = (e) => { if (e.key === 'Escape') close() }
        document.addEventListener('click', close)
        document.addEventListener('keydown', onKey)
        return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', onKey) }
    }, [menuId])

    useEffect(() => {
        if (flashId == null) return undefined
        const timer = setTimeout(() => setFlashId(null), 1600)
        return () => clearTimeout(timer)
    }, [flashId])

    // free the receipt preview when it changes or the page closes
    const receiptUrl = receipt && receipt.url
    useEffect(() => () => { if (receiptUrl) URL.revokeObjectURL(receiptUrl) }, [receiptUrl])

    const setRange = (range) => {
        const next = new URLSearchParams(location.search)
        next.set('from', range.from)
        next.set('to', range.to)
        history.replace({ pathname: location.pathname, search: '?' + next.toString() })
    }

    const choosePeriod = (key) => {
        if (key === 'custom') { setCustomOpen(true); return }
        setCustomOpen(false)
        setRange(periodRange(key))
    }

    const setCustomDate = (which, value) => {
        if (!ISO.test(value)) return
        const range = { from, to, [which]: value }
        if (range.from > range.to) {
            if (which === 'from') range.to = value
            else range.from = value
        }
        setRange(range)
    }

    const clearFilters = () => { setSearch(''); setQ(''); setCategory(''); setUnit('') }
    const filtered = Boolean(q || category || unit)

    // ---- editor ----

    const openEditor = (mode, expense) => {
        setMenuId(null)
        setFormError('')
        setProgress(null)
        if (!expense) {
            setForm({ ...EMPTY_FORM, spentOn: todayIso(), unit: unit || '', category: category || '' })
            setReceipt(null)
        } else {
            setForm({
                amount: expense.amount != null ? String(Number(expense.amount)) : '',
                spentOn: mode === 'duplicate' ? nextMonthIso(expense.spentOn) : expense.spentOn,
                category: expense.category || '', unit: expense.unit || '', title: expense.title || '',
                vendor: expense.vendor || '', billNo: mode === 'duplicate' ? '' : expense.billNo || '',
                paidBy: expense.paidBy || '', notes: expense.notes || ''
            })
            if (mode === 'edit' && expense.hasReceipt) {
                const type = expense.receiptType || ''
                setReceipt({ existing: true, name: expense.receiptName, type, loading: true })
                ExpenseService.getReceiptUrl(expense.id)
                    .then(url => setReceipt(r => (r && r.existing && r.loading ? { ...r, url, loading: false } : (URL.revokeObjectURL(url), r))))
                    .catch(() => setReceipt(r => (r && r.existing ? { ...r, loading: false, failed: true } : r)))
            } else {
                setReceipt(null)
            }
        }
        setEditor({ mode, expense: expense || null })
    }

    const closeEditor = () => {
        if (saving) return
        setEditor(null)
        setReceipt(null)
    }

    const field = (name) => (e) => setForm(f => ({ ...f, [name]: e.target.value }))
    const pick = (name, value) => setForm(f => ({ ...f, [name]: f[name] === value && name === 'paidBy' ? '' : value }))

    const chooseFile = (file) => {
        if (!file) return
        if (!RECEIPT_TYPES.includes(file.type) || file.size > MAX_RECEIPT) {
            setFormError('Receipts can be a photo or PDF up to 5 MB.')
            return
        }
        setFormError('')
        setReceipt({ file, name: file.name, type: file.type, url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null })
    }

    const removeFile = () => {
        setReceipt(r => (r && r.existing ? { removed: true } : null))
        if (fileRef.current) fileRef.current.value = ''
    }

    const validate = () => {
        const amount = Number(form.amount)
        if (!form.amount || !Number.isFinite(amount) || amount <= 0) return 'Please enter an amount above ₹0.'
        if (!/^\d+(\.\d{1,2})?$/.test(String(form.amount).trim())) return 'Amounts can have up to 2 decimal places.'
        if (amount > 10000000) return 'Amounts can be up to ₹1,00,00,000.'
        if (!ISO.test(form.spentOn)) return 'Please choose the date it was paid.'
        if (form.spentOn > todayIso()) return "The date can't be in the future."
        if (!form.category) return 'Please choose a category.'
        if (!form.unit) return 'Please choose where it was spent.'
        if (!form.title.trim()) return 'Please say what it was for.'
        return ''
    }

    const save = (addAnother) => {
        const problem = validate()
        if (problem) { setFormError(problem); return }
        setSaving(true)
        setFormError('')
        const id = editor && editor.mode === 'edit' ? editor.expense.id : null
        const payload = { ...form, amount: String(form.amount).trim(), paidBy: form.paidBy || null }
        let savedId = id
        ExpenseService.save(id, payload)
            .then(res => {
                savedId = res.data.data.id
                if (receipt && receipt.file) {
                    setProgress(0)
                    return ExpenseService.uploadReceipt(savedId, receipt.file, setProgress)
                        .catch(err => { throw Object.assign(err || {}, { receiptOnly: true }) })
                }
                if (receipt && receipt.removed && id) return ExpenseService.removeReceipt(savedId)
                return null
            })
            .then(() => {
                setToast(id ? 'Expense saved' : 'Expense added')
                setFlashId(savedId)
                loadList(0)
                loadSummary()
                if (addAnother) {
                    setForm(f => ({ ...EMPTY_FORM, spentOn: f.spentOn, category: f.category, unit: f.unit, paidBy: f.paidBy }))
                    setReceipt(null)
                    setEditor({ mode: 'new', expense: null })
                    if (amountRef.current) amountRef.current.focus()
                } else {
                    setEditor(null)
                    setReceipt(null)
                }
            })
            .catch(err => {
                if (err && err.receiptOnly) {
                    // the expense itself is saved: keep editing it so the receipt can be tried again
                    loadList(0)
                    loadSummary()
                    setEditor(e => ({ mode: 'edit', expense: { ...(e && e.expense), ...payload, id: savedId } }))
                    setFormError(serverMessage(err, 'The expense is saved, but the receipt did not upload. Please try again.'))
                } else {
                    setFormError(serverMessage(err, 'Could not save the expense. Please try again.'))
                }
            })
            .finally(() => { setSaving(false); setProgress(null) })
    }

    const doDelete = () => {
        if (!confirmDelete) return
        setDeleting(true)
        ExpenseService.remove(confirmDelete.id)
            .then(() => {
                setToast('Expense deleted')
                if (editor && editor.expense && editor.expense.id === confirmDelete.id) { setEditor(null); setReceipt(null) }
                setConfirmDelete(null)
                loadList(0)
                loadSummary()
            })
            .catch(err => {
                setConfirmDelete(c => ({ ...c, error: serverMessage(err, 'Could not delete the expense. Please try again.') }))
            })
            .finally(() => setDeleting(false))
    }

    // ---- view ----

    const days = useMemo(() => {
        const groups = []
        for (const e of (list && list.items) || []) {
            const last = groups[groups.length - 1]
            if (last && last.date === e.spentOn) last.items.push(e)
            else groups.push({ date: e.spentOn, items: [e] })
        }
        return groups.map(g => ({ ...g, total: g.items.reduce((t, e) => t + (Number(e.amount) || 0), 0) }))
    }, [list])

    const rangeLabel = from.slice(0, 7) === to.slice(0, 7) && from.endsWith('-01')
        ? formatMonth(from.slice(0, 7), true)
        : `${formatDay(from, { day: 'numeric', month: 'short', year: 'numeric' })} – ${formatDay(to, { day: 'numeric', month: 'short', year: 'numeric' })}`

    const editorTitle = !editor ? '' : editor.mode === 'edit' ? 'Edit expense' : editor.mode === 'duplicate' ? 'Duplicate expense' : 'Add expense'
    const isEdit = editor && editor.mode === 'edit'
    const panelBusy = saving || confirmDelete != null

    const renderForm = () => (
        <form id='exp-form' className='mv-form mv-exp-form' onSubmit={(e) => { e.preventDefault(); save(false) }} noValidate>
            {editor && editor.mode === 'duplicate' &&
                <p className='mv-alert mv-alert-info'>A copy dated a month later. Check the amount and date, then save.</p>}

            <div className='mv-field'>
                <label className='mv-label' htmlFor='exp-amount'>Amount</label>
                <div className='mv-exp-amount'>
                    <span aria-hidden='true'>₹</span>
                    <input id='exp-amount' ref={amountRef} className='mv-input' inputMode='decimal' autoComplete='off' placeholder='0'
                        value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value.replace(/[^\d.]/g, '') }))} />
                </div>
            </div>

            <div className='mv-form-grid mv-exp-two'>
                <div className='mv-field'>
                    <label className='mv-label' htmlFor='exp-date'>Paid on</label>
                    <input id='exp-date' type='date' className='mv-input' max={todayIso()} min='2020-01-01'
                        value={form.spentOn} onChange={field('spentOn')} />
                </div>
                <div className='mv-field'>
                    <label className='mv-label' htmlFor='exp-paid'>Paid by <span className='mv-label-optional'>(optional)</span></label>
                    <select id='exp-paid' className='mv-select' value={form.paidBy} onChange={field('paidBy')}>
                        <option value=''>Not set</option>
                        {PAID_BY.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                </div>
            </div>

            <fieldset className='mv-exp-fieldset'>
                <legend className='mv-label'>Category</legend>
                {EXPENSE_GROUPS.map(g =>
                    <div key={g.key} className='mv-exp-cat-group'>
                        <span className='mv-exp-cat-group-label'>{g.label}</span>
                        <div className='mv-exp-cats'>
                            {EXPENSE_CATEGORIES.filter(c => c.group === g.key).map(c =>
                                <label key={c.key} className={'mv-exp-cat' + (form.category === c.key ? ' active' : '')}>
                                    <input type='radio' name='exp-category' value={c.key} checked={form.category === c.key}
                                        onChange={() => pick('category', c.key)} />
                                    <span className='mv-exp-cat-icon' aria-hidden='true'>{c.icon}</span>
                                    <span className='mv-exp-cat-text'><strong>{c.label}</strong><small>{c.hint}</small></span>
                                </label>)}
                        </div>
                    </div>)}
            </fieldset>

            <fieldset className='mv-exp-fieldset'>
                <legend className='mv-label'>Where</legend>
                <div className='mv-exp-where'>
                    {EXPENSE_UNITS.map(u =>
                        <label key={u.key} className={'mv-exp-where-opt' + (form.unit === u.key ? ' active' : '')}>
                            <input type='radio' name='exp-unit' value={u.key} checked={form.unit === u.key} onChange={() => pick('unit', u.key)} />
                            <span aria-hidden='true'>{u.icon}</span>{u.label}
                        </label>)}
                </div>
            </fieldset>

            <div className='mv-field'>
                <label className='mv-label' htmlFor='exp-title'>What for</label>
                <input id='exp-title' className='mv-input' maxLength={120} placeholder='e.g. Cotton fabric, 40 m'
                    value={form.title} onChange={field('title')} />
            </div>

            <div className='mv-form-grid mv-exp-two'>
                <div className='mv-field'>
                    <label className='mv-label' htmlFor='exp-vendor'>Paid to <span className='mv-label-optional'>(optional)</span></label>
                    <input id='exp-vendor' className='mv-input' maxLength={120} list='exp-vendors' placeholder='Vendor, brand or person'
                        value={form.vendor} onChange={field('vendor')} />
                    <datalist id='exp-vendors'>{brands.map(b => <option key={b.id} value={b.name} />)}</datalist>
                </div>
                <div className='mv-field'>
                    <label className='mv-label' htmlFor='exp-bill'>Bill / invoice no. <span className='mv-label-optional'>(optional)</span></label>
                    <input id='exp-bill' className='mv-input' maxLength={60} value={form.billNo} onChange={field('billNo')} />
                </div>
            </div>

            <div className='mv-field'>
                <label className='mv-label' htmlFor='exp-notes'>Notes <span className='mv-label-optional'>(optional)</span></label>
                <textarea id='exp-notes' className='mv-textarea mv-exp-notes' maxLength={500} value={form.notes} onChange={field('notes')} />
            </div>

            <div className='mv-field'>
                <span className='mv-label'>Receipt <span className='mv-label-optional'>(optional)</span></span>
                {receipt && !receipt.removed ?
                    <div className='mv-exp-receipt'>
                        {receipt.loading ? <Skeleton width={56} height={56} radius='10px' /> :
                            receipt.url && receipt.type.startsWith('image/') ?
                                <img src={receipt.url} alt='Receipt preview' className='mv-exp-receipt-thumb' /> :
                                <span className='mv-exp-receipt-file' aria-hidden='true'><HiOutlineDocumentText /></span>}
                        <span className='mv-exp-receipt-text'>
                            <strong>{receipt.name || 'Receipt'}</strong>
                            {progress != null ? <span className='mv-exp-progress'><span style={{ width: progress + '%' }} /></span> :
                                receipt.failed ? <small>Could not load the receipt.</small> :
                                    receipt.url && receipt.existing ?
                                        <a href={receipt.url} target='_blank' rel='noopener noreferrer'>Open <HiOutlineExternalLink aria-hidden='true' /></a> :
                                        <small>{receipt.file ? 'Uploads when you save' : ''}</small>}
                        </span>
                        <button type='button' className='mv-icon-btn' aria-label='Remove receipt' onClick={removeFile} disabled={saving}><HiX /></button>
                    </div> :
                    <label className='mv-exp-drop' htmlFor='exp-file'
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); chooseFile(e.dataTransfer.files && e.dataTransfer.files[0]) }}>
                        <HiOutlineUpload aria-hidden='true' />
                        <span><strong>Add a photo or PDF</strong><small>Up to 5 MB · only staff can open it</small></span>
                    </label>}
                <input id='exp-file' ref={fileRef} type='file' className='mv-visually-hidden' accept='image/jpeg,image/png,image/webp,application/pdf'
                    onChange={(e) => chooseFile(e.target.files && e.target.files[0])} />
            </div>

            {formError && <div className='mv-alert mv-alert-error' role='alert'>{formError}</div>}
        </form>
    )

    const footer = (
        <>
            {isEdit &&
                <button type='button' className='mv-icon-btn mv-inv-delete' aria-label='Delete' title='Delete' disabled={saving}
                    onClick={() => setConfirmDelete(editor.expense)}>
                    <HiOutlineTrash />
                </button>}
            <span className='mv-panel-spacer' />
            {!isEdit &&
                <button type='button' className='mv-btn mv-btn-secondary mv-exp-another' disabled={saving} onClick={() => save(true)}>
                    Save &amp; add another
                </button>}
            <button type='submit' form='exp-form' className='mv-btn mv-btn-primary' disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save'}
            </button>
        </>
    )

    const hasItems = list && list.items && list.items.length > 0

    return (
        <main className='mv-page mv-exp'>
            <div className='mv-page-head'>
                <div>
                    <h1 className='mv-list-title'>Expenses</h1>
                    <p className='mv-page-sub'>Every rupee the business spends: stock, tailoring, people and places.</p>
                </div>
                <button type='button' className='mv-btn mv-btn-primary mv-btn-sm' onClick={() => openEditor('new')}>
                    <HiOutlinePlus aria-hidden='true' /> Add expense
                </button>
            </div>

            <div className='mv-exp-period'>
                <div className='mv-chip-row' role='group' aria-label='Period'>
                    {PERIODS.map(p =>
                        <button type='button' key={p.key} className={!customOpen && period === p.key ? 'mv-filter-chip active' : 'mv-filter-chip'}
                            aria-pressed={!customOpen && period === p.key} onClick={() => choosePeriod(p.key)}>{p.label}</button>)}
                    <button type='button' className={customOpen || period === 'custom' ? 'mv-filter-chip active' : 'mv-filter-chip'}
                        aria-pressed={customOpen || period === 'custom'} onClick={() => choosePeriod('custom')}>Custom</button>
                </div>
                {(customOpen || period === 'custom') &&
                    <div className='mv-exp-dates'>
                        <label>From <input type='date' className='mv-input' value={from} max={to} onChange={e => setCustomDate('from', e.target.value)} /></label>
                        <label>To <input type='date' className='mv-input' value={to} min={from} onChange={e => setCustomDate('to', e.target.value)} /></label>
                    </div>}
                <p className='mv-exp-range'>{rangeLabel}</p>
            </div>

            {isAdmin ?
                <Summary summary={summary} loading={summaryLoading && !summary} category={category} unit={unit}
                    onCategory={setCategory} onUnit={setUnit} /> :
                <p className='mv-exp-private'>
                    <HiOutlineLockClosed aria-hidden='true' /> You see the expenses you added. Totals and profit are visible to admins.
                </p>}

            <div className='mv-exp-bar-row'>
                <div className='mv-search'>
                    <HiOutlineSearch aria-hidden='true' />
                    <input type='search' className='mv-input' placeholder='Search what for, vendor or bill no.' aria-label='Search expenses'
                        value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select className='mv-select mv-exp-cat-filter' aria-label='Category' value={category} onChange={e => setCategory(e.target.value)}>
                    <option value=''>All categories</option>
                    {EXPENSE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <div className='mv-chip-row mv-exp-unit-chips' role='group' aria-label='Where'>
                    <button type='button' className={!unit ? 'mv-filter-chip active' : 'mv-filter-chip'} aria-pressed={!unit} onClick={() => setUnit('')}>All</button>
                    {EXPENSE_UNITS.map(u =>
                        <button type='button' key={u.key} className={unit === u.key ? 'mv-filter-chip active' : 'mv-filter-chip'}
                            aria-pressed={unit === u.key} onClick={() => setUnit(unit === u.key ? '' : u.key)}>{u.label}</button>)}
                </div>
            </div>

            <div className='mv-exp-list-head'>
                <span>
                    {list ? `${list.totalCount} ${list.totalCount === 1 ? 'expense' : 'expenses'}` : ' '}
                    {isAdmin && list && list.sum != null && filtered && <> · <strong>{formatRupee(list.sum)}</strong></>}
                </span>
                {filtered && <button type='button' className='mv-link-btn' onClick={clearFilters}><HiX aria-hidden='true' /> Clear filters</button>}
            </div>

            {listError && !list &&
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load expenses'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={() => loadList(0)} />}

            {!listError && !list && <ListSkeleton />}

            {list && !hasItems && (filtered ?
                <EmptyState icon={<HiOutlineSearch />} title='Nothing matches'
                    text='Try another search, category or place.' actionLabel='Clear filters' onAction={clearFilters} /> :
                <EmptyState icon={<HiOutlineReceiptTax />} title='No expenses in this period'
                    text='Add fabric bills, wages, rent and anything else the business pays for.'
                    actionLabel='Add expense' onAction={() => openEditor('new')} />)}

            {hasItems &&
                <div className='mv-exp-list'>
                    {days.map(day =>
                        <section key={day.date} className='mv-exp-day' aria-label={formatDay(day.date)}>
                            <h2 className='mv-exp-day-head'>
                                <span>{formatDay(day.date)}</span>
                                {isAdmin && <span className='mv-exp-day-total'>{formatRupee(day.total)}</span>}
                            </h2>
                            <ul className='mv-exp-day-card'>
                                {day.items.map(e =>
                                    <ExpenseRow key={e.id} expense={e} isAdmin={isAdmin} menuOpen={menuId === e.id} flash={flashId === e.id}
                                        onOpen={(x) => openEditor('edit', x)} onMenu={setMenuId}
                                        onDuplicate={(x) => openEditor('duplicate', x)}
                                        onDelete={(x) => { setMenuId(null); setConfirmDelete(x) }} />)}
                            </ul>
                        </section>)}
                    {list.page + 1 < list.totalPages &&
                        <button type='button' className={'mv-btn mv-btn-secondary mv-exp-more' + (loadingMore ? ' is-busy' : '')}
                            disabled={loadingMore} onClick={() => loadList(list.page + 1)}>
                            {loadingMore ? 'Loading…' : `Show more (${list.totalCount - list.items.length} left)`}
                        </button>}
                </div>}

            <SidePanel open={editor != null} title={editorTitle} onClose={closeEditor} busy={panelBusy} footer={footer}>
                {renderForm()}
            </SidePanel>

            {confirmDelete &&
                <Modal title='Delete this expense?' onClose={() => !deleting && setConfirmDelete(null)} busy={deleting}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' disabled={deleting} onClick={() => setConfirmDelete(null)}>Cancel</button>
                        <button type='button' className='mv-btn mv-btn-danger-solid' disabled={deleting} onClick={doDelete}>
                            {deleting ? 'Deleting…' : 'Delete'}
                        </button>
                    </>}>
                    <p className='mv-modal-text'>
                        <strong>{confirmDelete.title}</strong> · {formatRupee(confirmDelete.amount)} on {formatDay(confirmDelete.spentOn)}.
                        {confirmDelete.hasReceipt ? ' Its receipt is deleted too.' : ''} This can't be undone.
                    </p>
                    {confirmDelete.error && <div className='mv-alert mv-alert-error' role='alert'>{confirmDelete.error}</div>}
                </Modal>}

            <Toast message={toast} onDone={() => setToast('')} />
        </main>
    )
}

export default Expenses
